import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Icon from "@mdi/react";
import {
    mdiHomeAccount,
    mdiAirplaneTakeoff,
    mdiCalendarRemove,
    mdiMenu,
    mdiPencil,
    mdiChevronLeft,
    mdiChevronRight,
    mdiChevronUp,
    mdiChevronDown,
    mdiCalendarToday,
    mdiPlus,
    mdiTrashCan,
    mdiAccountLock,
    mdiMedicalBag,
    mdiHelpCircleOutline,
    mdiHelpCircle,
} from "@mdi/js";
import { DateTime } from "luxon";
import { useAuth } from "../../contexts/AuthContext";
import useTravelManagement from "../../hooks/useTravelManagement";
import { availabilityApi } from "../../api/availability";
import { bookingApi } from "../../api/booking";
import { instructorApi } from "../../api/instructor";
import { organizationApi } from "../../api/organization";
import AppHeader from "../../components/AppHeader";
import WeeklyScheduleModal from "../../components/WeeklyScheduleModal";
import AddSlotsModal from "../../components/AddSlotsModal";
import TravelPeriodModal from "../../components/TravelPeriodModal";
import TravelAvailabilitySetupModal from "../../components/TravelAvailabilitySetupModal";
import EditSlotsModal from "../../components/EditSlotsModal";
import SickDayConfirmModal from "../../components/SickDayConfirmModal";
import CancelTravelPeriodModal from "../../components/CancelTravelPeriodModal";
import AvailabilityHelpModal from "../../components/AvailabilityHelpModal";
import EditWeeklyScheduleModal from "../../components/EditWeeklyScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import addSlotHoverHint from "../../assets/mouse_hand_web1.jpg";
import "../Common.css";
import "../Dashboard.css";
import "../header.css";
import "./ManageAvailabilityUnified.css";

/**
 * ManageAvailabilityUnified Component
 *
 * MIGRATION NOTE (2026-02-07): Migrated to CustomFormInput component
 * Part of DL-04FEB26-01: CustomFormInput migration (Extended Scope)
 *
 * Migrated inputs:
 * - Day selector checkboxes (7 checkboxes, turquoise variant)
 * - Lesson duration radio buttons (4 radios, purple variant)
 */
const BLOCK_TYPE_LABELS = {
    holiday: "Public Holiday",
    leave: "Annual Leave",
    personal: "Personal Day",
};

const ManageAvailabilityUnified = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Phase 1: Core state management
    // Phones start collapsed: the sidebar stacks above the grid there, so open it would
    // push the week off-screen. Matches the 600px phone block in ManageAvailabilityUnified.css.
    const [sidebarExpanded, setSidebarExpanded] = useState(() => !window.matchMedia("(max-width: 600px)").matches);
    const [blockedDates, setBlockedDates] = useState([]);
    const [sickDays, setSickDays] = useState([]);
    const [teachingLocations, setTeachingLocations] = useState([]);
    const [orgCurrency, setOrgCurrency] = useState("AUD"); // org base currency — the default for a new travel period's rate
    // Instructor's default private-lesson rate in DOLLARS (hourlyRate.amount is cents ÷ 100).
    // '' when unset. Pre-fills a new travel period's rate as the editable default to override.
    const [defaultHourlyRate, setDefaultHourlyRate] = useState("");
    const [mode, setMode] = useState("home"); // 'home' | 'travel'

    // Modal state - split into two separate modals
    const [showWeeklyScheduleModal, setShowWeeklyScheduleModal] = useState(false);
    const [showAddHomeModal, setShowAddHomeModal] = useState(false);
    const [addHomeDate, setAddHomeDate] = useState(null); // fixed date when opened from a grid cell
    const [addHomePrefillTime, setAddHomePrefillTime] = useState(null); // prefilled start time from the clicked cell
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [helpIconHovered, setHelpIconHovered] = useState(false);
    const [showEditScheduleModal, setShowEditScheduleModal] = useState(false);
    const [editingScheduleGroup, setEditingScheduleGroup] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null); // { title, message, variant, onConfirm }

    // Phase 3: Week navigation state (Google Bookings-style week grid)
    // Calculate the Sunday that starts the week containing today
    const [currentWeekStart, setCurrentWeekStart] = useState(() => {
        const today = DateTime.local();
        const dayOfWeek = today.weekday; // 1=Mon, 2=Tue, ..., 7=Sun
        const daysFromSunday = dayOfWeek === 7 ? 0 : dayOfWeek; // If Sunday, 0 days back; else weekday number
        return today.minus({ days: daysFromSunday }).startOf("day");
    });

    // Smart time range: 6am-10pm covers most instructor schedules
    const DEFAULT_START_HOUR = 6; // 6 AM
    const DEFAULT_END_HOUR = 22; // 10 PM

    // Generate hours array: [6, 7, 8, ..., 22] = 17 hours
    const hours = React.useMemo(
        () => Array.from({ length: DEFAULT_END_HOUR - DEFAULT_START_HOUR + 1 }, (_, i) => i + DEFAULT_START_HOUR),
        []
    );

    // ⚙️ TIMELINE HEIGHT CONFIGURATION (must match CSS)
    // To adjust: Update BOTH this constant AND ManageAvailabilityUnified.css:
    // 1. This constant (PIXELS_PER_HOUR)
    // 2. .time-marker height
    // 3. .cards-container min-height = 13 hours × PIXELS_PER_HOUR
    const PIXELS_PER_HOUR = 45; // Must match CSS .time-marker height

    // Helper: Convert time string to minutes since start of day
    const timeToMinutes = timeString => {
        const [hours, minutes] = timeString.split(":").map(Number);
        return hours * 60 + (minutes || 0);
    };

    // Calculate slot height based on duration
    const calculateHeight = durationMinutes => {
        const fullHeight = durationMinutes * (PIXELS_PER_HOUR / 60);
        return Math.max(fullHeight - 8, 32); // Subtract 8px (4px top + 4px bottom spacing)
    };

    // Calculate vertical position from start time (relative to 6 AM start)
    const calculatePosition = startTime => {
        const startMinutes = timeToMinutes(startTime);
        const dayStartMinutes = DEFAULT_START_HOUR * 60; // 6 AM = 360 minutes
        const minutesFromStart = startMinutes - dayStartMinutes;
        const basePosition = minutesFromStart * (PIXELS_PER_HOUR / 60);
        return basePosition + 4; // Add 4px offset for visual breathing room from hour lines
    };

    // Convert 24hr time to 12hr format
    const formatTime12hr = time24 => {
        const [hours, minutes] = time24.split(":").map(Number);
        const period = hours >= 12 ? "pm" : "am";
        const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        return minutes > 0 ? `${hours12}:${minutes.toString().padStart(2, "0")}${period}` : `${hours12}${period}`;
    };

    // Phase 3: Travel period selection for week view highlighting
    const [selectedTravelPeriod, setSelectedTravelPeriod] = useState(null);

    // Context-aware week start: Sunday for home mode, day before travel start for travel mode
    const contextualWeekStart = React.useMemo(() => {
        if (mode === "travel" && selectedTravelPeriod) {
            // Travel mode: Start week on day before travel period
            const travelStart = DateTime.fromISO(selectedTravelPeriod.startDate).startOf("day");
            return travelStart.minus({ days: 1 });
        }
        // Home mode: Use current Sunday-based week start
        return currentWeekStart;
    }, [mode, selectedTravelPeriod, currentWeekStart]);

    // Generate 7 days of the week - Memoized for performance
    // Home mode: Sun-Sat based on calendar weeks
    // Travel mode: Day before travel start + 6 days
    const weekDays = React.useMemo(
        () => Array.from({ length: 7 }, (_, i) => contextualWeekStart.plus({ days: i })),
        [contextualWeekStart]
    );

    // Phase 4: Availability data state
    const [calendarData, setCalendarData] = useState(null);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [recurringSchedules, setRecurringSchedules] = useState([]);
    const [loadingSchedules, setLoadingSchedules] = useState(false);

    // Phase 4.5: Booking data state (for conflict prevention)
    const [bookings, setBookings] = useState([]); // Confirmed/pending bookings

    // Phase 5: Week time window editing state (sidebar cards, not grid clicks)

    // Day editor modal state (travel mode only - allows editing individual day slots)
    const [editingDate, setEditingDate] = useState(null);

    const [editingOverrideId, setEditingOverrideId] = useState(null);

    // Slots edit modal state — shared by home + travel (granular slot management)
    const [showEditHomeModal, setShowEditHomeModal] = useState(false);
    const [editingDayOfWeek, setEditingDayOfWeek] = useState(null);

    // Empty travel-day flow: clicking the full-day ghost on a travel day with NO slots
    // opens the multi-slot day builder directly. Once the day has ≥1 slot the ghost is
    // gone and the per-hour + cells take over for single adds. null = builder closed.
    const [builderDate, setBuilderDate] = useState(null);

    // Local state for travel management messages
    const [, setSuccessMessage] = useState("");
    const [, setError] = useState("");

    // Sick day state
    const [showSickDayModal, setShowSickDayModal] = useState(false);
    const [isSickDayProcessing, setIsSickDayProcessing] = useState(false);
    const [todaysBookings, setTodaysBookings] = useState([]);

    // useTravelManagement hook provides all state and CRUD operations
    const {
        travelPeriods,
        setTravelPeriods,
        showTravelModal,
        openTravelModal,
        openTravelModalForEdit,
        closeTravelModal,
        handleSaveTravelPeriod,
        editingPeriod,
        showAvailabilitySetupModal,
        setShowAvailabilitySetupModal,
        availabilitySetupPeriod,
        setAvailabilitySetupPeriod,
        handleDeleteTravelPeriod,
        showDeleteTravelModal,
        confirmDeleteTravelPeriod,
        cancelDeleteTravelPeriod,
        showCancelTravelModal,
        showOverlapWarning,
        overlappingPeriods,
        pendingTravelPeriod,
        handleConfirmOverlap,
        handleCancelOverlap,
        affectedBookings,
        isCancelDeleteProcessing,
        handleConfirmCancelAndDelete,
        handleCloseCancelTravelModal,
        showPastPeriods,
        setShowPastPeriods,
        visibleTravelPeriods,
        homeBookingConflicts,
        showHomeBookingConflict,
        isConflictCancelProcessing,
        handleConfirmConflictCancel,
        handleCloseConflictModal,
        handleKeepConflicts,
        unlockBookings,
        showUnlockCancelModal,
        isUnlockProcessing,
        lockRefreshKey,
        openUnlockCancel,
        closeUnlockCancel,
        handleConfirmUnlockCancel,
    } = useTravelManagement({
        availabilityApi,
        bookingApi,
        user,
        setSuccessMessage,
        setError,
    });

    // Helper: Calculate Sunday that starts the week containing a given date
    const calculateSundayWeekStart = (date = DateTime.local()) => {
        const dayOfWeek = date.weekday; // 1=Mon, 2=Tue, ..., 7=Sun
        const daysFromSunday = dayOfWeek === 7 ? 0 : dayOfWeek;
        return date.minus({ days: daysFromSunday }).startOf("day");
    };

    // Load travel periods on mount
    useEffect(() => {
        if (user?.organisationId) {
            availabilityApi.getTravelPeriods(user.organisationId).then(setTravelPeriods).catch(console.error);
        }
    }, [user]);

    // Load the org base currency — it's the default currency for a new travel period's rate.
    useEffect(() => {
        if (!user?.organisationId) return;
        organizationApi
            .getOrgProfile(user.organisationId)
            .then(org => setOrgCurrency(org.currency || "AUD"))
            .catch(() => {});
    }, [user?.organisationId]);

    // Load blocked date contexts and sick day overrides on mount
    useEffect(() => {
        if (!user?.organisationId) return;
        Promise.all([availabilityApi.getContexts(), availabilityApi.getOverrides(user.organisationId)])
            .then(([contexts, overrides]) => {
                const blocked = contexts
                    .filter(ctx => ctx.context === "holiday" || ctx.context === "leave" || ctx.context === "personal")
                    .map(ctx => ({
                        id: ctx.id,
                        startDate: ctx.startDate,
                        endDate: ctx.endDate,
                        context: ctx.context,
                        reason: ctx.reason || "",
                        color: ctx.color || "#999999",
                    }));
                setBlockedDates(blocked);

                const sick = overrides
                    .filter(o => o.isClosed && o.source === "sick_day")
                    .map(o => ({
                        id: o.id,
                        date: DateTime.fromISO(o.date).toISODate(),
                        reason: o.reason || "",
                    }));
                setSickDays(sick);
            })
            .catch(console.error);
    }, [user]);

    // Load teaching locations for travel period venue dropdown.
    // Backend wraps the array under `instructorProfile.preferredLocations` —
    // see backend-R1/src/express/routes/api/v2/instructor/profile/profile.service.js:89
    useEffect(() => {
        if (user?.organisationId) {
            instructorApi
                .getProfile(user.organisationId)
                .then(profile => {
                    setTeachingLocations(profile.instructorProfile?.preferredLocations || []);
                    // hourlyRate.amount is integer CENTS; travel privatePrice is DOLLARS — convert.
                    const rateAmount = profile.instructorProfile?.hourlyRate?.amount;
                    setDefaultHourlyRate(rateAmount ? String(rateAmount / 100) : "");
                })
                .catch(console.error);
        }
    }, [user]);

    // Sync selectedTravelPeriod when travelPeriods refreshes (after edit or delete)
    useEffect(() => {
        if (!selectedTravelPeriod) return;
        const updated = travelPeriods.find(p => p.id === selectedTravelPeriod.id);
        if (!updated) {
            setSelectedTravelPeriod(null); // period was deleted
        } else if (updated !== selectedTravelPeriod) {
            setSelectedTravelPeriod(updated); // period was edited — sync to fresh data
        }
    }, [travelPeriods]); // intentionally omits selectedTravelPeriod — only runs when list refreshes

    // Clear selectedTravelPeriod when switching to home mode
    useEffect(() => {
        if (mode === "home") {
            setSelectedTravelPeriod(null);
            // CRITICAL FIX: Reset week start to current Sunday when returning to home mode
            const sundayStart = calculateSundayWeekStart();
            setCurrentWeekStart(sundayStart);
        }
    }, [mode]);

    // Phase 4.1: Load recurring schedules for home mode sidebar
    useEffect(() => {
        const loadRecurringSchedules = async () => {
            if (!user?.organisationId) return;

            setLoadingSchedules(true);
            try {
                const schedules = await availabilityApi.getRecurringSchedules(user.organisationId);
                setRecurringSchedules(schedules);
            } catch (error) {
                console.error("Failed to load recurring schedules:", error);
                toast.error(error.message || "Failed to load recurring schedules");
            } finally {
                setLoadingSchedules(false);
            }
        };

        loadRecurringSchedules();
    }, [user]);

    // Phase 4: Load availability slots from backend
    // Wrapped in useCallback to ensure stable reference and proper dependency tracking
    const loadAvailabilitySlots = React.useCallback(async () => {
        if (!user?.organisationId) return;

        // CRITICAL: Travel mode requires a selected travel period
        // Skip API call if in travel mode but no period selected
        if (mode === "travel" && !selectedTravelPeriod) {
            setCalendarData(null); // Clear calendar to show empty state
            setLoadingSlots(false);
            return;
        }

        setCalendarData(null); // Clear immediately so stale slots don't show during load
        setLoadingSlots(true);

        try {
            // Calculate week range
            // Home mode: Sunday to Saturday
            // Travel mode: Day before travel start + 6 days
            const weekEnd = contextualWeekStart.plus({ days: 6 });
            const startDate = contextualWeekStart.toISODate();
            const endDate = weekEnd.toISODate();

            // Get contextId if travel mode and period selected
            const contextId = mode === "travel" && selectedTravelPeriod ? selectedTravelPeriod.id : null;

            // Get contextId if travel mode and period selected
            // Call backend API: GET /api/v2/availability/management-calendar
            const calendar = await availabilityApi.getManagementCalendar(startDate, endDate, mode, contextId);

            setCalendarData(calendar);
        } catch (error) {
            console.error("Failed to load availability slots:", error);
            toast.error(error.message || "Failed to load availability data");
        } finally {
            setLoadingSlots(false);
        }
    }, [user?.organisationId, mode, selectedTravelPeriod, contextualWeekStart]);

    // Load availability slots when week or mode changes
    useEffect(() => {
        loadAvailabilitySlots();
    }, [loadAvailabilitySlots]);

    // Phase 4.5: Load bookings for conflict prevention + the sick-day modal.
    // Lifted to a useCallback so it can be re-run after a sick day cancels/refunds
    // bookings (otherwise the stale list keeps showing just-cancelled bookings).
    const loadBookings = React.useCallback(async () => {
        if (!user?.organisationId) return;

        try {
            // Load all instructor bookings
            const allBookings = await bookingApi.getInstructorBookings();

            // Active bookings only (status is pending | confirmed | cancelled | completed —
            // "paid" is NOT a status; paid-ness is derived from pricing below).
            const transformed = allBookings
                .filter(b => ["pending", "confirmed"].includes(b.status))
                .map(booking => {
                    // Paid-ness lives in pricing.paymentStatus / creditApplied, NOT in
                    // status ("paid" is not a status) — a prepaid booking is
                    // status:'confirmed' + paymentStatus:'paid'.
                    const isPaid =
                        ["paid", "refunded", "credited"].includes(booking.pricing?.paymentStatus) ||
                        (booking.pricing?.creditApplied || 0) > 0;
                    const ep = booking.effectivePricing || {};
                    return {
                        id: booking.id,
                        date: booking.bookingDate, // ISO string "2026-02-10T09:00:00Z"
                        startTime: booking.timeSlot.startTime, // "09:00"
                        endTime: booking.timeSlot.endTime, // "10:00"
                        status: booking.status,
                        paymentStatus: booking.pricing?.paymentStatus || null,
                        creditApplied: booking.pricing?.creditApplied || 0,
                        // Sick-day paid-booking decision UI needs these:
                        isPaid,
                        isGuest: booking.studentId === "000000000000000000000000",
                        studentName: booking.studentName || booking.studentInfo?.firstName || "Student",
                        // Principal (lesson + venue, excl. platform service fee) in INTEGER CENTS.
                        // effectivePricing.* fields are cents — format with formatCurrencyFromMinor.
                        // What the refund/credit action can return; the backend caps the actual
                        // return at the refundable amount regardless.
                        refundableAmount: (ep.lessonFee || 0) + (ep.venueFee || 0),
                        currency: ep.currency || booking.pricing?.currency || "AUD",
                    };
                });

            setBookings(transformed);
        } catch (error) {
            console.error("Failed to load bookings:", error);
            // Non-fatal - continue without booking display
        }
    }, [user?.organisationId]);

    useEffect(() => {
        loadBookings();
    }, [loadBookings]);

    // Toggle sidebar collapse
    const handleToggleSidebar = () => {
        setSidebarExpanded(!sidebarExpanded);
    };

    // Handle mode tab switching
    const handleModeChange = newMode => {
        if (newMode === "home") {
            setShowPastPeriods(false);
        }
        setMode(newMode);
    };

    // Phase 3: Week navigation handlers
    const handlePrevWeek = () => {
        setCurrentWeekStart(prev => prev.minus({ weeks: 1 }));
    };

    const handleNextWeek = () => {
        setCurrentWeekStart(prev => prev.plus({ weeks: 1 }));
    };

    // Format week range for display header
    const formatWeekRange = () => {
        const weekEnd = contextualWeekStart.plus({ days: 6 });
        if (contextualWeekStart.month === weekEnd.month) {
            return `${contextualWeekStart.toFormat("MMM d")} - ${weekEnd.toFormat("d, yyyy")}`;
        }
        return `${contextualWeekStart.toFormat("MMM d")} - ${weekEnd.toFormat("MMM d, yyyy")}`;
    };

    // Jump to current week (Today button)
    const handleTodayClick = () => {
        const today = DateTime.local();
        const dayOfWeek = today.weekday;
        const daysFromSunday = dayOfWeek === 7 ? 0 : dayOfWeek;
        const sunday = today.minus({ days: daysFromSunday }).startOf("day");
        setCurrentWeekStart(sunday);
    };

    // Navigation boundary logic - Prevent navigating to past weeks
    const isPrevWeekDisabled = React.useMemo(() => {
        const prevWeekStart = contextualWeekStart.minus({ weeks: 1 });
        const prevWeekEnd = prevWeekStart.plus({ days: 6 });
        const today = DateTime.local().startOf("day");

        // Always disable if previous week ends before today (past date prevention)
        if (prevWeekEnd < today) {
            return true;
        }

        // If travel period selected, also check travel period boundaries
        if (mode === "travel" && selectedTravelPeriod) {
            const travelStart = DateTime.fromISO(selectedTravelPeriod.startDate).startOf("day");
            // Disable if previous week ends before travel period starts
            return prevWeekEnd < travelStart;
        }

        return false;
    }, [contextualWeekStart, mode, selectedTravelPeriod]);

    /**
     * Set CSS variable for grid height calculation
     * Allows responsive CSS to adjust based on visible hour count
     */
    React.useEffect(() => {
        const hourCount = DEFAULT_END_HOUR - DEFAULT_START_HOUR + 1; // 17 hours
        document.documentElement.style.setProperty("--grid-hours-count", hourCount);
    }, []);

    /**
     * Auto-scroll to 9 AM position on calendar load and mode changes
     * Scrolls the grid scrollable container to show typical working hours
     * (6-8 AM remain accessible by scrolling up)
     */
    React.useEffect(() => {
        if (calendarData && !loadingSlots) {
            const timeoutId = setTimeout(() => {
                const scrollableContainer = document.querySelector(".week-grid-scrollable");

                if (scrollableContainer) {
                    // Calculate scroll position: 9 AM relative to 6 AM start
                    // (9 - 6) × 45px = 135px from top
                    const scrollPosition = (9 - DEFAULT_START_HOUR) * PIXELS_PER_HOUR;

                    // Scroll the container to 9 AM position
                    scrollableContainer.scrollTo({
                        top: scrollPosition,
                        behavior: "smooth",
                    });
                }
            }, 500);

            return () => clearTimeout(timeoutId);
        }
    }, [calendarData, loadingSlots, mode]);

    // Navigation boundary logic - Prevent navigating outside travel period
    const isNextWeekDisabled = React.useMemo(() => {
        // Only check travel period boundaries for next week (no future date limit for home mode)
        if (mode === "travel" && selectedTravelPeriod) {
            const nextWeekStart = contextualWeekStart.plus({ weeks: 1 });
            const travelEnd = DateTime.fromISO(selectedTravelPeriod.endDate).startOf("day");

            // Disable if next week starts after travel period ends
            return nextWeekStart > travelEnd;
        }

        return false;
    }, [contextualWeekStart, mode, selectedTravelPeriod]);

    // Check if a day falls within the selected travel period
    const isDayInSelectedPeriod = day => {
        if (!selectedTravelPeriod) return false;

        const periodStart = DateTime.fromISO(selectedTravelPeriod.startDate).startOf("day");
        const periodEnd = DateTime.fromISO(selectedTravelPeriod.endDate).startOf("day");
        const checkDay = day.startOf("day");

        return checkDay >= periodStart && checkDay <= periodEnd;
    };

    // Phase 4.5: Check if a specific hour on a specific day is covered by a booking.
    // Must be an OVERLAP test, not a start-time match: a multi-hour booking (e.g.
    // 09:00-12:00) covers the 9am, 10am AND 11am cells. A start-time-only check
    // (booking.startTime === "10:00") left the 10am/11am cells rendering as
    // "available" and clickable on top of an existing booking — a double-booking
    // hole. Mirrors the backend overlap mark in views.service.js.
    const isSlotBooked = React.useCallback(
        (day, hour) => {
            const dateString = day.toISODate(); // "2026-02-10"
            const cellStart = `${String(hour).padStart(2, "0")}:00`; // "09:00"
            const cellEnd = `${String(hour + 1).padStart(2, "0")}:00`; // "10:00"

            return bookings.some(booking => {
                const bookingDate = booking.date ? booking.date.split("T")[0] : "";
                if (bookingDate !== dateString) return false;
                if (!["pending", "confirmed"].includes(booking.status)) return false;
                // Overlap: booking starts before the cell ends AND ends after the cell starts.
                // booking.endTime is always present (set from timeSlot.endTime at load).
                return booking.startTime < cellEnd && booking.endTime > cellStart;
            });
        },
        [bookings]
    );

    // Handle weekly schedule creation
    const handleScheduleCreated = async () => {
        setShowWeeklyScheduleModal(false);
        // Reload calendar and recurring schedules to show newly published schedule
        await loadAvailabilitySlots();
        // Reload recurring schedules list
        try {
            const schedules = await availabilityApi.getRecurringSchedules(user.organisationId);
            setRecurringSchedules(schedules);
        } catch (error) {
            console.error("Error reloading schedules:", error);
        }
    };

    // Open the add-availability modal. With no args = sidebar entry (date picker shown);
    // with (date, startTime) = grid empty-cell entry (date fixed, start prefilled).
    const openAddHomeModal = (date = null, startTime = null) => {
        setAddHomeDate(date);
        setAddHomePrefillTime(startTime);
        setShowAddHomeModal(true);
    };

    const closeAddHomeModal = () => {
        setShowAddHomeModal(false);
        setAddHomeDate(null);
        setAddHomePrefillTime(null);
    };

    // Add availability for a single day. Always POST (createOverride upsert-merges
    // additively) — never PUT, which would authoritatively replace the day's slots.
    // travelPeriodId is supplied by the modal (null for home, the trip id for travel).
    const handleAddHomeSlots = async ({ date, slots, travelPeriodId = null }) => {
        try {
            await availabilityApi.createOverride({
                date,
                slots,
                travelPeriodId,
                source: "manual",
            });
            toast.success(travelPeriodId ? "Travel availability added" : "Home availability added");
            closeAddHomeModal();
            await loadAvailabilitySlots();
        } catch (error) {
            console.error("Error adding slots:", error);
            toast.error(error.message || "Failed to add availability");
        }
    };

    // "Make room": shift the colliding slots forward (up to a gap) and add the new ones.
    // travelPeriodId is NOT sent: the nudge endpoint's schema rejects unknown keys, and
    // the service keeps the existing override's tag itself (a nudged day always has an
    // override already — that's where its slots live).
    const handleNudgeHomeSlots = async ({ date, addSlots, shifts }) => {
        try {
            await availabilityApi.nudgeSlots({ date, addSlots, shifts });
            toast.success("Made room and added your times");
            closeAddHomeModal();
            await loadAvailabilitySlots();
        } catch (error) {
            console.error("Error nudging slots:", error);
            toast.error(error.message || error.response?.data?.message || "Failed to make room");
        }
    };

    // Handle editing a schedule group
    const handleEditSchedule = group => {
        setEditingScheduleGroup(group);
        setShowEditScheduleModal(true);
    };

    // Handle deleting a recurring schedule (single doc delete)
    const handleDeleteSchedule = schedule => {
        setConfirmModal({
            title: "Delete Schedule",
            message: `Delete "${schedule.scheduleName || "this schedule"}"? This cannot be undone.`,
            variant: "danger",
            onConfirm: async () => {
                setConfirmModal(null);
                try {
                    await availabilityApi.deleteRecurringSchedule(schedule.id);

                    toast.success(`Deleted schedule "${schedule.scheduleName || "schedule"}"`);

                    // Reload calendar and schedules list
                    await loadAvailabilitySlots();
                    const schedules = await availabilityApi.getRecurringSchedules(user.organisationId);
                    setRecurringSchedules(schedules);
                } catch (error) {
                    console.error("Error deleting schedule:", error);
                    toast.error("Failed to delete schedule");
                }
            },
        });
    };

    // Phase 2.8: Format date range for card display
    const formatDateRange = (startDate, endDate) => {
        const start = DateTime.fromISO(startDate);
        const end = DateTime.fromISO(endDate);
        return `${start.toFormat("MMM d")} - ${end.toFormat("MMM d, yyyy")}`;
    };

    // Phase 4: Helper function to determine slot color based on mode
    // Note: Backend returns simple slots (startTime, endTime, duration, available)
    // The mode (home vs travel) determines which slots appear, not slot properties
    const getSlotColor = () => {
        // Color based on current mode since backend filters by mode
        return mode === "travel"
            ? "var(--dl-color-purple-900)" // Purple for travel mode
            : "var(--dl-color-turquoise-default)"; // Turquoise for home mode
    };

    // Phase 5: Week grid is READ-ONLY display
    // Editing happens via sidebar time window cards (implemented below)

    /**
     * Apply day edits from EditSlotsModal (shifts + removals) via the nudge endpoint.
     * shifts[] move slots (block old + add new); blockedSlots[] block recurring times;
     * removedSlots[] drop ad-hoc slots. All day-specific — the weekly pattern is untouched.
     */
    const handleApplyHomeEdits = async ({ date, shifts, blockedSlots, removedSlots, travelPeriodId = null }) => {
        try {
            // travelPeriodId is used for the toast wording only — the nudge endpoint
            // rejects unknown keys and preserves the existing override's tag itself.
            await availabilityApi.nudgeSlots({
                date,
                shifts: shifts || [],
                blockedSlots: blockedSlots || [],
                removedSlots: removedSlots || [],
                addSlots: [],
            });

            toast.success(
                travelPeriodId ? "Travel availability updated successfully" : "Home availability updated successfully"
            );

            // Close the modal BEFORE reloading so it unmounts before calendarData is
            // cleared during the refetch (otherwise it flashes the empty state).
            setShowEditHomeModal(false);
            setEditingDate(null);
            setEditingOverrideId(null);
            setEditingDayOfWeek(null);

            await loadAvailabilitySlots();
        } catch (error) {
            console.error("Error updating home slots:", error);
            toast.error(error.message || error.response?.data?.message || "Failed to update availability");
        }
    };

    // Extract city and country from location description
    // e.g., "Adelaide SA, Australia" -> { city: "Adelaide SA", country: "Australia" }
    const extractCityAndCountry = description => {
        if (!description) return { city: "", country: "" };

        const parts = description.split(",").map(part => part.trim());

        if (parts.length === 0) return { city: "", country: "" };
        if (parts.length === 1) return { city: parts[0], country: "" };

        const city = parts[0]; // First part is city (may include state/region)
        const country = parts[parts.length - 1]; // Last part is always the country

        return { city, country };
    };

    /**
     * Active bookings for a given ISO date, formatted for the sick day modal.
     * Includes BOTH unpaid and paid bookings — the modal handles them differently:
     * unpaid ones are bulk-cancelled with the sick day, paid ones require a per-booking
     * refund/credit/keep decision (sick day is a block, not a refund path, so a paid
     * booking cannot just be silently cancelled). Paid-ness is pricing.paymentStatus /
     * creditApplied, NOT status. Sorted by start time (earliest first).
     */
    const getSickDayBookingsForDate = dateIso => {
        return bookings
            .filter(booking => {
                const bookingDate = booking.date.split("T")[0];
                return bookingDate === dateIso && ["pending", "confirmed"].includes(booking.status);
            })
            .map(booking => ({
                id: booking.id,
                status: booking.status,
                startTime: booking.startTime,
                isPaid: booking.isPaid,
                isGuest: booking.isGuest,
                studentName: booking.studentName,
                refundableAmount: booking.refundableAmount,
                currency: booking.currency,
            }))
            .sort((a, b) => a.startTime.localeCompare(b.startTime));
    };

    /**
     * Handle sick day button click
     * Opens modal showing today's bookings that will be cancelled
     */
    const handleSickDay = () => {
        // Default to today — user can change in the modal (which re-derives the list
        // via onDateChange) before confirming
        setTodaysBookings(getSickDayBookingsForDate(DateTime.local().toISODate()));
        setShowSickDayModal(true);
    };

    /**
     * Confirm sick day from modal.
     *
     * Two kinds of bookings are handled in one flow:
     *  - Unpaid bookings (selectedBookingIds): bulk-cancelled by the sick-day API.
     *  - Paid bookings (paidDecisions): each carries an instructor decision —
     *    'refund' / 'credit' (issued via issueReturn as a FULL return, which cancels
     *    the booking) or 'keep' (left live; the instructor will reschedule directly).
     *
     * Paid returns are processed FIRST so the money action + cancellation happen through
     * the proper refund path (the sick-day API deliberately refuses paid bookings), then
     * the day is blocked. A failed return does NOT abort the sick day — it's reported so
     * the instructor can retry that one booking from its detail view.
     *
     * @param {string} cancellationMessage
     * @param {string[]} selectedBookingIds - unpaid booking ids to cancel
     * @param {string} date - ISO sick-day date
     * @param {Array<{bookingId: string, action: 'refund'|'credit'|'keep'}>} paidDecisions
     */
    const handleConfirmSickDay = async (cancellationMessage, selectedBookingIds, date, paidDecisions = []) => {
        try {
            setIsSickDayProcessing(true);
            setError("");
            setSuccessMessage("");

            // 1. Process paid-booking refund/credit decisions (full return → cancels booking).
            //    'keep' is a no-op here (booking stays live by the instructor's choice).
            let returnsIssued = 0;
            const returnFailures = [];
            for (const decision of paidDecisions) {
                if (decision.action !== "refund" && decision.action !== "credit") continue;
                try {
                    // No explicit amount → full remaining refundable amount, which cancels
                    // the booking. A partial would leave it live on a blocked day.
                    await bookingApi.issueReturn(decision.bookingId, {
                        type: decision.action,
                        message: cancellationMessage,
                    });
                    returnsIssued += 1;
                } catch (err) {
                    console.error(`Sick day: failed to ${decision.action} booking ${decision.bookingId}:`, err);
                    returnFailures.push(decision.action);
                }
            }

            // 2. Block the day + cancel the unpaid bookings.
            await availabilityApi.sickDay({
                date,
                reason: cancellationMessage,
                bookingIds: selectedBookingIds,
            });

            // Close modal
            setShowSickDayModal(false);

            // Show success message reflecting both cancellations and money returns.
            const parts = ["Sick day recorded"];
            if (selectedBookingIds.length > 0) {
                parts.push(`${selectedBookingIds.length} booking(s) cancelled`);
            }
            if (returnsIssued > 0) {
                parts.push(`${returnsIssued} refund/credit issued`);
            }
            if (selectedBookingIds.length === 0 && returnsIssued === 0) {
                parts.push("availability blocked");
            }
            const message = `${parts.join(", ")}.`;

            if (returnFailures.length > 0) {
                toast.error(
                    `${message} But ${returnFailures.length} refund/credit failed. Resolve those bookings individually.`
                );
            } else {
                toast.success(message);
            }

            // Reload availability slots and bookings (paid bookings may have just been
            // cancelled via refund/credit; unpaid ones via the sick-day API).
            await loadAvailabilitySlots();
            await loadBookings();

            // Reload sick days to show the new overlay
            availabilityApi
                .getOverrides(user.organisationId)
                .then(overrides => {
                    const sick = overrides
                        .filter(o => o.isClosed && o.source === "sick_day")
                        .map(o => ({
                            id: o.id,
                            date: DateTime.fromISO(o.date).toISODate(),
                            reason: o.reason || "",
                        }));
                    setSickDays(sick);
                })
                .catch(console.error);

            setTimeout(() => setSuccessMessage(""), 5000);
        } catch (error) {
            console.error("Error recording sick day:", error);
            toast.error(error.message || "Failed to record sick day");
        } finally {
            setIsSickDayProcessing(false);
        }
    };

    return (
        <div className="dashboard-container org-console">
            {/* Standard Navigation Header - EXACT copy from InstructorPortalDashboard.js */}
            <AppHeader />

            {/* Main Content Area - Two-column layout */}
            <div className="dashboard-main">
                <div className="dashboard-content-wrapper full-width">
                    <div className="unified-page-content" data-mode={mode}>
                        {/* Left Sidebar */}
                        <aside className={`unified-sidebar ${sidebarExpanded ? "expanded" : "collapsed"}`}>
                            {/* Collapse Toggle Button */}
                            <button
                                onClick={handleToggleSidebar}
                                className="sidebar-toggle"
                                aria-label={sidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
                            >
                                {/* Chevron shows which way the sidebar will move. Both directions
                                    render; CSS shows the side pair (left/right) beside the grid and
                                    the stacked pair (up/down) when the sidebar sits above it. */}
                                {sidebarExpanded && (
                                    <Icon
                                        path={mdiChevronLeft}
                                        size={0.7}
                                        color="var(--dl-color-purple-default)"
                                        className="sidebar-toggle-chevron--side"
                                    />
                                )}
                                <Icon path={mdiMenu} size={1} color="var(--dl-color-purple-default)" />
                                {!sidebarExpanded && (
                                    <Icon
                                        path={mdiChevronRight}
                                        size={0.7}
                                        color="var(--dl-color-purple-default)"
                                        className="sidebar-toggle-chevron--side"
                                    />
                                )}
                                <Icon
                                    path={sidebarExpanded ? mdiChevronUp : mdiChevronDown}
                                    size={0.8}
                                    color="var(--dl-color-purple-default)"
                                    className="sidebar-toggle-chevron--stacked"
                                />
                            </button>

                            {/* Mode Selector Tabs */}
                            <div className="mode-tabs">
                                <button
                                    onClick={() => handleModeChange("home")}
                                    className={`mode-tab ${mode === "home" ? "active" : ""}`}
                                    aria-label="Home Schedule"
                                >
                                    <Icon path={mdiHomeAccount} size={1} color={mode === "home" ? "#451f78" : "#666"} />
                                    {sidebarExpanded && <span className="mode-tab-label">Home Schedule</span>}
                                </button>

                                <button
                                    onClick={() => handleModeChange("travel")}
                                    className={`mode-tab ${mode === "travel" ? "active" : ""}`}
                                    aria-label="Travel Schedule"
                                >
                                    <Icon
                                        path={mdiAirplaneTakeoff}
                                        size={1}
                                        color={mode === "travel" ? "#451f78" : "#666"}
                                    />
                                    {sidebarExpanded && <span className="mode-tab-label">Travel Schedule</span>}
                                </button>
                            </div>

                            {/* Dynamic Settings Panel */}
                            <div className="sidebar-settings-panel">
                                {mode === "home" ? (
                                    <>
                                        <div className="settings-panel-header">
                                            <div className="settings-panel-title-row">
                                                <h3 className="settings-panel-title">Manage Availability</h3>
                                                <button
                                                    className="help-icon-btn"
                                                    onClick={() => setShowHelpModal(true)}
                                                    onMouseEnter={() => setHelpIconHovered(true)}
                                                    onMouseLeave={() => setHelpIconHovered(false)}
                                                    aria-label="Help - How availability management works"
                                                    title="How It Works"
                                                >
                                                    <Icon
                                                        path={helpIconHovered ? mdiHelpCircle : mdiHelpCircleOutline}
                                                        size={0.9}
                                                        color="var(--dl-color-purple-default)"
                                                    />
                                                </button>
                                            </div>
                                            <p className="settings-panel-subtitle">
                                                Weekly schedule is your default. Use one-off (ad-hoc) adjustments to
                                                change specific days.
                                            </p>
                                        </div>

                                        {/* Weekly Schedule Action Button */}
                                        <div className="weekly-schedule-action">
                                            <button
                                                className="weekly-schedule-btn"
                                                onClick={() => setShowWeeklyScheduleModal(true)}
                                                aria-label="Set weekly schedule"
                                            >
                                                <span className="weekly-schedule-btn-label">Set weekly schedule</span>
                                                <div className="create-availability-icon-btn">
                                                    <Icon path={mdiPlus} size={0.85} color="white" />
                                                </div>
                                            </button>
                                            <p className="action-hint">Repeats each week.</p>
                                        </div>

                                        {/* "Add one-off availability" button retired: the grid is the add
                                            surface (clicking an empty cell opens the same modal with date +
                                            time prefilled). Restore by uncommenting if a sidebar entry point
                                            is ever needed again (e.g. off-grid hours before 6am/after 10pm).
                                        <div className="one-time-override-action">
                                            <button
                                                className="one-time-override-btn"
                                                onClick={() => openAddHomeModal()}
                                                aria-label="Add availability">
                                                <span className="one-time-override-btn-label">
                                                    Add one-off availability
                                                </span>
                                                <div className="create-availability-icon-btn">
                                                    <Icon path={mdiPlus} size={0.85} color="white" />
                                                </div>
                                            </button>
                                        </div>
                                        */}

                                        {/* One-off hours hint — teaches the grid gesture the button used to duplicate */}
                                        <div className="one-time-override-action">
                                            <p className="action-hint">
                                                <strong>To add slots</strong> outside your weekly schedule, hover over
                                                an empty time on a day and click the{" "}
                                                <span className="add-slot-hint-plus">+</span>.
                                            </p>
                                            <img
                                                src={addSlotHoverHint}
                                                alt="Hovering an empty time on the grid shows a + to add availability"
                                                className="add-slot-hover-hint-img"
                                            />
                                            <p className="action-hint add-slot-remove-hint">
                                                <strong>To remove a slot</strong>, click it on the day.
                                            </p>
                                        </div>

                                        {/* Block Dates Action Button */}
                                        <div className="block-dates-action">
                                            <button
                                                className="block-dates-btn"
                                                onClick={() => navigate("/instructor-profile-setup/block-dates")}
                                                aria-label="Block dates"
                                            >
                                                <span className="block-dates-btn-label">Block dates</span>
                                                <div className="create-availability-icon-btn">
                                                    <Icon path={mdiCalendarRemove} size={0.85} color="white" />
                                                </div>
                                            </button>
                                            <p className="action-hint">Block holidays, vacations, and personal time.</p>
                                        </div>

                                        {/* This Week's Published Schedules - Filtered and Grouped */}
                                        {(() => {
                                            if (loadingSchedules) {
                                                return (
                                                    <div className="loading-schedules">
                                                        <p>Loading schedules...</p>
                                                    </div>
                                                );
                                            }

                                            if (recurringSchedules.length === 0) {
                                                return null;
                                            }

                                            const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

                                            // Filter recurring schedules to current week
                                            // New schema: each doc has windows[] — check if any window's dayOfWeek is in the current week
                                            const weekRecurringSchedules = recurringSchedules.filter(schedule => {
                                                const scheduleDayInWeek = schedule.windows?.some(w =>
                                                    weekDays.some(day => {
                                                        const luxonWeekday = day.weekday; // 1-7 (Monday=1, Sunday=7)
                                                        const dayOfWeekIndex = luxonWeekday === 7 ? 0 : luxonWeekday;
                                                        return dayOfWeekIndex === w.dayOfWeek;
                                                    })
                                                );

                                                // Check if validity period overlaps with current week
                                                const scheduleStart = DateTime.fromISO(schedule.validFrom).startOf(
                                                    "day"
                                                );
                                                const scheduleEnd = DateTime.fromISO(schedule.validUntil).startOf(
                                                    "day"
                                                );
                                                const weekStart = currentWeekStart.startOf("day");
                                                const weekEnd = currentWeekStart.plus({ days: 6 }).startOf("day");
                                                const overlapsWeek =
                                                    scheduleStart <= weekEnd && scheduleEnd >= weekStart;

                                                return scheduleDayInWeek && overlapsWeek;
                                            });

                                            if (weekRecurringSchedules.length === 0) {
                                                return null;
                                            }

                                            // New schema: each schedule doc IS a schedule period (no grouping needed)
                                            return (
                                                <>
                                                    <div className="week-recurring-schedules">
                                                        <h4 className="week-schedules-title">
                                                            This Week's Published Schedules
                                                        </h4>
                                                        <p className="week-schedules-subtitle">
                                                            {currentWeekStart.toFormat("MMM d")} -{" "}
                                                            {currentWeekStart.plus({ days: 6 }).toFormat("MMM d")}
                                                        </p>
                                                        {weekRecurringSchedules.map((schedule, index) => {
                                                            // A day may carry MULTIPLE windows (slot groups /
                                                            // break splits) — dedupe to unique day names. The
                                                            // card shows days + the schedule's date range only;
                                                            // per-day times live in the edit modal.
                                                            const windows = schedule.windows || [];
                                                            const days = [...new Set(windows.map(w => w.dayOfWeek))]
                                                                .sort((a, b) => a - b)
                                                                .map(d => DAY_NAMES[d])
                                                                .join(", ");
                                                            return (
                                                                <div key={index} className="week-recurring-group-card">
                                                                    <div className="schedule-card-header">
                                                                        <div className="schedule-name">
                                                                            {schedule.scheduleName ||
                                                                                "Unnamed Schedule"}
                                                                        </div>
                                                                        <div className="schedule-actions">
                                                                            <button
                                                                                className="schedule-action-btn edit-btn"
                                                                                onClick={() =>
                                                                                    handleEditSchedule(schedule)
                                                                                }
                                                                                title="Edit schedule"
                                                                            >
                                                                                <Icon path={mdiPencil} size={0.7} />
                                                                            </button>
                                                                            <button
                                                                                className="schedule-action-btn delete-btn"
                                                                                onClick={() =>
                                                                                    handleDeleteSchedule(schedule)
                                                                                }
                                                                                title="Delete schedule"
                                                                            >
                                                                                <Icon path={mdiTrashCan} size={0.7} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    <div className="schedule-days">{days}</div>
                                                                    <div className="schedule-dates">
                                                                        {DateTime.fromISO(schedule.validFrom).toFormat(
                                                                            "MMM d"
                                                                        )}{" "}
                                                                        -{" "}
                                                                        {DateTime.fromISO(schedule.validUntil).toFormat(
                                                                            "MMM d, yyyy"
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </>
                                ) : (
                                    <>
                                        {/* Header */}
                                        <div className="settings-panel-header">
                                            <div className="settings-panel-title-row">
                                                <h3 className="settings-panel-title">Set Travel Availability</h3>
                                                <button
                                                    className="help-icon-btn"
                                                    onClick={() => setShowHelpModal(true)}
                                                    onMouseEnter={() => setHelpIconHovered(true)}
                                                    onMouseLeave={() => setHelpIconHovered(false)}
                                                    aria-label="Help - How travel availability works"
                                                    title="How It Works"
                                                >
                                                    <Icon
                                                        path={helpIconHovered ? mdiHelpCircle : mdiHelpCircleOutline}
                                                        size={0.9}
                                                        color="var(--dl-color-purple-default)"
                                                    />
                                                </button>
                                            </div>
                                            <p className="settings-panel-subtitle">
                                                Set availability when travelling to teach at other locations.
                                            </p>

                                            {/* Add Button - Above separator */}
                                            <button className="add-travel-period-btn" onClick={openTravelModal}>
                                                + Add Travel Period
                                            </button>
                                        </div>

                                        {/* Travel Periods List */}
                                        <div className="travel-periods-list">
                                            {travelPeriods.length === 0 ? (
                                                <div className="empty-state">
                                                    <p>No travel periods configured.</p>
                                                    <p className="hint">
                                                        Click "Add Travel Period" to add your first travel schedule.
                                                    </p>
                                                </div>
                                            ) : (
                                                <>
                                                    {travelPeriods.some(
                                                        p =>
                                                            DateTime.fromISO(p.endDate) <
                                                            DateTime.local().startOf("day")
                                                    ) && (
                                                        <button
                                                            className="past-periods-toggle-btn"
                                                            onClick={() => setShowPastPeriods(prev => !prev)}
                                                        >
                                                            {showPastPeriods ? "Hide past" : "Show past"}
                                                        </button>
                                                    )}
                                                    {visibleTravelPeriods.map((period, idx) => {
                                                        const today = DateTime.local().startOf("day");
                                                        const isPastPeriod = DateTime.fromISO(period.endDate) < today;
                                                        const prevPeriod = visibleTravelPeriods[idx - 1];
                                                        const isFirstPastItem =
                                                            isPastPeriod &&
                                                            (!prevPeriod ||
                                                                DateTime.fromISO(prevPeriod.endDate) >= today);
                                                        const showPastDivider = showPastPeriods && isFirstPastItem;

                                                        // Get color from backend (from shared TRAVEL_COLORS palette)
                                                        const color = period.color || "var(--dl-color-purple-900)";

                                                        // Extract city and country for display
                                                        // Use location.address for full address format (e.g., "Adelaide SA, Australia")
                                                        const { city, country } = extractCityAndCountry(
                                                            period.location?.address ||
                                                                period.location?.description ||
                                                                period.destination
                                                        );

                                                        // Check if this period is currently selected
                                                        const isActive = selectedTravelPeriod?.id === period.id;
                                                        const isPast =
                                                            DateTime.fromISO(period.endDate) <
                                                            DateTime.local().startOf("day");

                                                        return (
                                                            <React.Fragment key={period.id}>
                                                                {showPastDivider && (
                                                                    <div className="travel-past-divider">
                                                                        <div className="sidebar-section-separator" />
                                                                        <span className="travel-past-divider-label">
                                                                            Past
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                <div
                                                                    className={`travel-period-card ${isActive ? "active" : ""}`}
                                                                    onClick={() => {
                                                                        // Toggle selection: clicking selected period deselects it
                                                                        setSelectedTravelPeriod(
                                                                            isActive ? null : period
                                                                        );

                                                                        // Auto-navigate to show travel period
                                                                        // Travel mode: Week starts day before travel period start
                                                                        // This allows users to see the transition into travel
                                                                        if (!isActive) {
                                                                            const travelStart = DateTime.fromISO(
                                                                                period.startDate
                                                                            ).startOf("day");
                                                                            const weekStart = travelStart.minus({
                                                                                days: 1,
                                                                            });
                                                                            setCurrentWeekStart(weekStart);
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        borderColor: color,
                                                                        borderWidth: "2px",
                                                                        backgroundColor: isActive
                                                                            ? color
                                                                            : `${color}15`, // Solid when active, 15% opacity when inactive
                                                                    }}
                                                                >
                                                                    <div className="travel-period-icon">
                                                                        <Icon
                                                                            path={mdiAirplaneTakeoff}
                                                                            size={1}
                                                                            color={isActive ? "white" : color}
                                                                        />
                                                                    </div>
                                                                    <div className="travel-period-content">
                                                                        <div className="travel-period-title">
                                                                            {city}
                                                                            {country && (
                                                                                <span className="travel-period-title-country">
                                                                                    , {country}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="travel-period-dates">
                                                                            {formatDateRange(
                                                                                period.startDate,
                                                                                period.endDate
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="travel-period-actions">
                                                                        {!isPast && (
                                                                            <button
                                                                                className="travel-period-icon-button"
                                                                                title="Edit"
                                                                                onClick={e => {
                                                                                    e.stopPropagation();
                                                                                    openTravelModalForEdit(period);
                                                                                }}
                                                                            >
                                                                                <Icon
                                                                                    path={mdiPencil}
                                                                                    size={0.7}
                                                                                    color={isActive ? "white" : color}
                                                                                />
                                                                            </button>
                                                                        )}
                                                                        <button
                                                                            className="travel-period-icon-button"
                                                                            title="Delete"
                                                                            onClick={e => {
                                                                                e.stopPropagation();
                                                                                handleDeleteTravelPeriod(period.id);
                                                                            }}
                                                                        >
                                                                            <Icon
                                                                                path={mdiTrashCan}
                                                                                size={0.7}
                                                                                color={isActive ? "white" : color}
                                                                            />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </aside>

                        {/* Right Main Area */}
                        <main className="unified-main-area">
                            {/* Week Navigation Header - Three-cluster layout */}
                            <div className="week-nav-header">
                                {/* Left Cluster: Navigation */}
                                <button
                                    type="button"
                                    onClick={() => navigate("/instructor-dashboard")}
                                    className="back-nav-btn week-back-link"
                                >
                                    ← Instructor Dashboard
                                </button>

                                {/* Center Cluster: Time Navigation */}
                                <div className="week-time-navigation">
                                    <button
                                        onClick={handlePrevWeek}
                                        className="week-nav-btn"
                                        aria-label="Previous week"
                                        disabled={isPrevWeekDisabled}
                                    >
                                        <Icon path={mdiChevronLeft} size={1} />
                                    </button>

                                    <h2 className="week-range-display">{formatWeekRange()}</h2>

                                    <button
                                        onClick={handleNextWeek}
                                        className="week-nav-btn"
                                        aria-label="Next week"
                                        disabled={isNextWeekDisabled}
                                    >
                                        <Icon path={mdiChevronRight} size={1} />
                                    </button>
                                </div>

                                {/* Right Cluster: Actions */}
                                <div className="week-action-buttons">
                                    {/* Today button — home mode only; superfluous in travel mode (period selector drives navigation) */}
                                    {mode === "home" && (
                                        <button
                                            onClick={handleTodayClick}
                                            className="week-today-btn"
                                            aria-label="Jump to current week"
                                            title="Jump to current week"
                                        >
                                            <Icon path={mdiCalendarToday} size={0.9} />
                                            <span>Today</span>
                                        </button>
                                    )}

                                    <button
                                        className="btn-sick-day"
                                        onClick={handleSickDay}
                                        aria-label="I'm Sick Today"
                                        title="Record sick day and cancel today's bookings"
                                    >
                                        <Icon path={mdiMedicalBag} size={0.8} />
                                    </button>
                                </div>
                            </div>

                            {/* Week Grid Container */}
                            <div className="week-grid-container">
                                {/* Compact Availability Legend - INSIDE grid container */}
                                <div className="availability-legend-compact">
                                    {mode === "home" && (
                                        <>
                                            <div className="legend-item">
                                                <span className="legend-color home-available"></span>
                                                <span className="legend-label">Available (Recurring)</span>
                                            </div>
                                            <div className="legend-item">
                                                <span className="legend-color home-available-adhoc"></span>
                                                <span className="legend-label">Available (Ad-hoc)</span>
                                            </div>
                                        </>
                                    )}
                                    {mode === "travel" && (
                                        <div className="legend-item">
                                            <span className="legend-color travel-available"></span>
                                            <span className="legend-label">Available</span>
                                        </div>
                                    )}
                                    <div className="legend-item">
                                        <span className="legend-color booked"></span>
                                        <span className="legend-label">Booked</span>
                                    </div>
                                </div>

                                {/* Travel Mode - No Period Selected Empty State */}
                                {mode === "travel" && !selectedTravelPeriod && !loadingSlots && (
                                    <div className="empty-state-message">
                                        <Icon
                                            path={mdiAirplaneTakeoff}
                                            size={2.5}
                                            color="var(--dl-color-purple-900)"
                                            className="empty-state-icon"
                                        />
                                        <h3>No Travel Period Selected</h3>
                                        <p>
                                            Select a travel period from the sidebar to view and manage your travel
                                            availability.
                                        </p>
                                    </div>
                                )}

                                {/* 8-column grid: 1 time ruler + 7 day columns */}
                                {(mode === "home" || (mode === "travel" && selectedTravelPeriod)) && (
                                    <>
                                        {/* Fixed Headers Row */}
                                        <div className="week-headers-fixed">
                                            {/* Empty corner for time ruler column */}
                                            <div className="week-header-corner"></div>

                                            {/* Day Headers */}
                                            {weekDays.map((day, dayIndex) => {
                                                const isInSelectedPeriod = isDayInSelectedPeriod(day);
                                                const today = DateTime.local().startOf("day");
                                                const isToday = day.startOf("day").equals(today);

                                                return (
                                                    <div
                                                        key={dayIndex}
                                                        className={`week-day-header ${mode === "travel" && isInSelectedPeriod ? "in-selected-period" : ""}`}
                                                        style={
                                                            mode === "travel" && isInSelectedPeriod
                                                                ? { "--period-color": selectedTravelPeriod.color }
                                                                : {}
                                                        }
                                                    >
                                                        <div className="week-day-name">{day.toFormat("EEE")}</div>
                                                        <div className={`week-day-date ${isToday ? "today" : ""}`}>
                                                            {day.toFormat("d")}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Scrollable Grid */}
                                        <div className="week-grid-scrollable">
                                            {/* Loading Overlay — anchored to the scroll area so it sits
                                                below the header and tracks the visible grid */}
                                            {loadingSlots && (
                                                <div className="loading-overlay">
                                                    <div className="loading-spinner-box">
                                                        <div className="loading-spinner"></div>
                                                        <span className="loading-spinner-label">Loading...</span>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="week-grid">
                                                {/* Time Ruler Column (left side) - Hour markers only, NO header */}
                                                <div className="time-ruler">
                                                    {hours.map(hour => (
                                                        <div key={hour} className="time-marker" data-hour={hour}>
                                                            {hour === 0
                                                                ? "12am"
                                                                : hour === 12
                                                                  ? "12pm"
                                                                  : hour > 12
                                                                    ? `${hour - 12}pm`
                                                                    : `${hour}am`}
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Horizontal ruler lines spanning all days */}
                                                <div className="horizontal-rulers">
                                                    {hours.map((hour, index) => (
                                                        <div key={hour} className="horizontal-ruler-line"></div>
                                                    ))}
                                                </div>

                                                {/* Day Columns (Sun-Sat) - Floating slot architecture */}
                                                {weekDays.map((day, dayIndex) => {
                                                    const isInSelectedPeriod = isDayInSelectedPeriod(day);
                                                    const periodColor =
                                                        selectedTravelPeriod?.color || "var(--dl-color-purple-900)";
                                                    const isDayPast = day < DateTime.local().startOf("day");

                                                    // Get all slots for this day (not filtered by hour)
                                                    const dateKey = day.toISODate();
                                                    // Backend returns flat array: calendar[date] = [slots]
                                                    const daySlots = Array.isArray(calendarData?.[dateKey])
                                                        ? calendarData[dateKey]
                                                        : [];

                                                    return (
                                                        <div
                                                            key={dayIndex}
                                                            className={`day-column-wrapper ${isDayPast ? "is-past" : ""}`}
                                                            style={
                                                                mode === "travel" && isInSelectedPeriod
                                                                    ? { "--period-color": periodColor }
                                                                    : {}
                                                            }
                                                        >
                                                            {/* Cards Container - Relative positioned for absolute slot children */}
                                                            <div className="cards-container">
                                                                {/* Home mode: clickable empty-hour cells to add availability.
                                                                    Rendered BEFORE the slots so slots paint on top and keep
                                                                    their own click-to-edit; empty gaps fall through to here.
                                                                    Hover shows a "+"; tap works without hover (touch). */}
                                                                {mode === "home" &&
                                                                    !isDayPast &&
                                                                    !loadingSlots &&
                                                                    !travelPeriods.some(period => {
                                                                        const s = DateTime.fromISO(
                                                                            period.startDate
                                                                        ).startOf("day");
                                                                        const e = DateTime.fromISO(
                                                                            period.endDate
                                                                        ).startOf("day");
                                                                        const d = day.startOf("day");
                                                                        return d >= s && d <= e;
                                                                    }) &&
                                                                    hours.slice(0, -1).map(hour => {
                                                                        // No + on hours an existing slot touches — the exposed
                                                                        // sliver of a partially-covered hour reads as a bug.
                                                                        // Edge gaps stay reachable from an adjacent free hour
                                                                        // via the modal's -15/+15 nudge.
                                                                        const hourStart = hour * 60;
                                                                        const hourCovered = daySlots.some(s => {
                                                                            const sStart = timeToMinutes(s.startTime);
                                                                            const sEnd = s.endTime
                                                                                ? timeToMinutes(s.endTime)
                                                                                : sStart + (s.duration || 60);
                                                                            return (
                                                                                sStart < hourStart + 60 &&
                                                                                sEnd > hourStart
                                                                            );
                                                                        });
                                                                        if (hourCovered) return null;
                                                                        const startTime = `${String(hour).padStart(2, "0")}:00`;
                                                                        return (
                                                                            <div
                                                                                key={`empty-${hour}`}
                                                                                className="empty-hour-cell"
                                                                                style={{
                                                                                    top: `${(hour - DEFAULT_START_HOUR) * PIXELS_PER_HOUR}px`,
                                                                                    height: `${PIXELS_PER_HOUR}px`,
                                                                                }}
                                                                                onClick={() =>
                                                                                    openAddHomeModal(dateKey, startTime)
                                                                                }
                                                                                title={`Add availability at ${formatTime12hr(startTime)}`}
                                                                            >
                                                                                <span className="empty-hour-cell-plus">
                                                                                    <Icon path={mdiPlus} size={0.8} />
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                {/* Travel mode, day WITH slots: per-hour + cells on empty gaps,
                                                                    mirroring home. Opens the travel-aware AddSlotsModal (single add). */}
                                                                {mode === "travel" &&
                                                                    isInSelectedPeriod &&
                                                                    !isDayPast &&
                                                                    !loadingSlots &&
                                                                    daySlots.length > 0 &&
                                                                    hours.slice(0, -1).map(hour => {
                                                                        // Same rule as home: no + on slot-covered hours
                                                                        const hourStart = hour * 60;
                                                                        const hourCovered = daySlots.some(s => {
                                                                            const sStart = timeToMinutes(s.startTime);
                                                                            const sEnd = s.endTime
                                                                                ? timeToMinutes(s.endTime)
                                                                                : sStart + (s.duration || 60);
                                                                            return (
                                                                                sStart < hourStart + 60 &&
                                                                                sEnd > hourStart
                                                                            );
                                                                        });
                                                                        if (hourCovered) return null;
                                                                        const startTime = `${String(hour).padStart(2, "0")}:00`;
                                                                        return (
                                                                            <div
                                                                                key={`travel-empty-${hour}`}
                                                                                className="empty-hour-cell empty-hour-cell--travel"
                                                                                style={{
                                                                                    top: `${(hour - DEFAULT_START_HOUR) * PIXELS_PER_HOUR}px`,
                                                                                    height: `${PIXELS_PER_HOUR}px`,
                                                                                    "--period-color": periodColor,
                                                                                }}
                                                                                onClick={() =>
                                                                                    openAddHomeModal(dateKey, startTime)
                                                                                }
                                                                                title={`Add availability at ${formatTime12hr(startTime)}`}
                                                                            >
                                                                                <span className="empty-hour-cell-plus">
                                                                                    <Icon path={mdiPlus} size={0.8} />
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                {/* Travel mode, EMPTY day: a full-day ghost that prompts the user
                                                                    to set up several slots at once (day builder) or add one at a
                                                                    time. Restores the multi-slot-per-day capability for skip-now trips. */}
                                                                {mode === "travel" &&
                                                                    isInSelectedPeriod &&
                                                                    !isDayPast &&
                                                                    !loadingSlots &&
                                                                    daySlots.length === 0 && (
                                                                        <div
                                                                            className="travel-empty-day-ghost"
                                                                            style={{
                                                                                top: `${calculatePosition("08:00")}px`,
                                                                                height: `${calculateHeight(540)}px`,
                                                                                "--period-color": periodColor,
                                                                            }}
                                                                            onClick={() => setBuilderDate(dateKey)}
                                                                            title="No availability set up for this day"
                                                                        >
                                                                            <Icon path={mdiPlus} size={0.8} />
                                                                            <span>Set up this day</span>
                                                                        </div>
                                                                    )}
                                                                {/* Render floating slots with calculated positions (READ-ONLY DISPLAY) */}
                                                                {daySlots.map((slot, slotIndex) => {
                                                                    // Calculate position and height
                                                                    const topPosition = calculatePosition(
                                                                        slot.startTime
                                                                    );
                                                                    const slotHeight = calculateHeight(slot.duration);

                                                                    // Phase 4.5: Check if this slot has a confirmed booking
                                                                    const hour = parseInt(slot.startTime.split(":")[0]);
                                                                    const hasBooking = isSlotBooked(day, hour);

                                                                    // Determine if slot should be greyed out based on mode
                                                                    let shouldGreyOut = false;

                                                                    if (mode === "home") {
                                                                        // In home mode: grey out slots in ANY travel period
                                                                        shouldGreyOut = travelPeriods.some(period => {
                                                                            const periodStart = DateTime.fromISO(
                                                                                period.startDate
                                                                            ).startOf("day");
                                                                            const periodEnd = DateTime.fromISO(
                                                                                period.endDate
                                                                            ).endOf("day");
                                                                            const dayStart = day.startOf("day");
                                                                            return (
                                                                                dayStart >= periodStart &&
                                                                                dayStart <= periodEnd
                                                                            );
                                                                        });
                                                                    } else if (mode === "travel") {
                                                                        // In travel mode: grey out slots NOT in selected period
                                                                        shouldGreyOut = !isInSelectedPeriod;
                                                                    }

                                                                    return (
                                                                        <div
                                                                            key={slotIndex}
                                                                            className="floating-slot"
                                                                            style={{
                                                                                top: `${topPosition}px`,
                                                                                height: `${slotHeight}px`,
                                                                            }}
                                                                        >
                                                                            <div
                                                                                className={`availability-slot ${
                                                                                    shouldGreyOut
                                                                                        ? "greyed-out"
                                                                                        : hasBooking
                                                                                          ? "booked"
                                                                                          : "available"
                                                                                } clickable`}
                                                                                data-source={slot.source || "recurring"}
                                                                                style={{
                                                                                    ...(!shouldGreyOut && !hasBooking
                                                                                        ? {
                                                                                              border: `2px solid ${getSlotColor()}`,
                                                                                              color: getSlotColor(),
                                                                                          }
                                                                                        : {}),
                                                                                    cursor:
                                                                                        shouldGreyOut ||
                                                                                        hasBooking ||
                                                                                        isDayPast
                                                                                            ? "not-allowed"
                                                                                            : "pointer",
                                                                                }}
                                                                                onClick={() => {
                                                                                    // Prevent click on wrong mode slots
                                                                                    if (shouldGreyOut) return;

                                                                                    // Prevent click on booked slots (view-only)
                                                                                    if (hasBooking) return;

                                                                                    // Prevent editing past travel slots
                                                                                    if (isDayPast) return;

                                                                                    const dateISO = day.toISODate();
                                                                                    const dayData =
                                                                                        calendarData[dateISO];
                                                                                    const daySlots = Array.isArray(
                                                                                        dayData
                                                                                    )
                                                                                        ? dayData
                                                                                        : [];
                                                                                    const overrideId =
                                                                                        daySlots.length > 0
                                                                                            ? daySlots[0].overrideId
                                                                                            : null;

                                                                                    // Home and travel both open the SAME edit modal
                                                                                    // (EditSlotsModal, mode-aware via travelPeriod).
                                                                                    // Travel slots are the same availability_overrides
                                                                                    // docs; the modal render passes travelPeriod when in
                                                                                    // travel mode so the save carries travelPeriodId.
                                                                                    setEditingDate(dateISO);
                                                                                    setEditingOverrideId(overrideId);
                                                                                    setEditingDayOfWeek(
                                                                                        day.weekday % 7
                                                                                    ); // Luxon weekday: 1-7 → 0-6
                                                                                    setShowEditHomeModal(true);
                                                                                }}
                                                                                title={
                                                                                    hasBooking
                                                                                        ? `Booked: ${formatTime12hr(slot.startTime)} - ${formatTime12hr(slot.endTime)}`
                                                                                        : shouldGreyOut
                                                                                          ? "Cannot edit - switch to matching mode"
                                                                                          : `Click to edit ${formatTime12hr(slot.startTime)} - ${formatTime12hr(slot.endTime)}`
                                                                                }
                                                                            >
                                                                                {/* Phase 4.5: Show booked label (no time - ruler shows time) */}
                                                                                {hasBooking ? (
                                                                                    <span className="slot-booked-label">
                                                                                        <Icon
                                                                                            path={mdiAccountLock}
                                                                                            size={0.7}
                                                                                            color="#ff5f25"
                                                                                        />
                                                                                        BOOKED
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="slot-time">
                                                                                        {formatTime12hr(slot.startTime)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}

                                                                {/* Travel period overlay — home mode only, suppressed when blocked date wins */}
                                                                {mode === "home" &&
                                                                    (() => {
                                                                        const overlayPeriod = travelPeriods.find(
                                                                            period => {
                                                                                const s = DateTime.fromISO(
                                                                                    period.startDate
                                                                                ).startOf("day");
                                                                                const e = DateTime.fromISO(
                                                                                    period.endDate
                                                                                ).startOf("day");
                                                                                const d = day.startOf("day");
                                                                                return d >= s && d <= e;
                                                                            }
                                                                        );
                                                                        if (!overlayPeriod) return null;
                                                                        const hasBlock = blockedDates.some(ctx => {
                                                                            const s = DateTime.fromISO(
                                                                                ctx.startDate
                                                                            ).startOf("day");
                                                                            const e = DateTime.fromISO(
                                                                                ctx.endDate
                                                                            ).startOf("day");
                                                                            const d = day.startOf("day");
                                                                            return d >= s && d <= e;
                                                                        });
                                                                        if (hasBlock) return null;
                                                                        const color = overlayPeriod.color || "#d946ef";
                                                                        return (
                                                                            <div
                                                                                className="bwv-travel-overlay"
                                                                                style={{ "--period-color": color }}
                                                                                title={`Travel: ${overlayPeriod.location?.description || overlayPeriod.destination || "Travel Period"}`}
                                                                            >
                                                                                <Icon
                                                                                    path={mdiAirplaneTakeoff}
                                                                                    size={0.9}
                                                                                />
                                                                                <span>Travel Period</span>
                                                                                <span className="bwv-travel-overlay-dest">
                                                                                    {overlayPeriod.location
                                                                                        ?.description ||
                                                                                        overlayPeriod.destination ||
                                                                                        ""}
                                                                                </span>
                                                                                <span className="bwv-travel-overlay-hint">
                                                                                    Switch to Travel mode to view
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    })()}

                                                                {/* Blocked date overlay — home mode only */}
                                                                {mode === "home" &&
                                                                    (() => {
                                                                        const blockCtx = blockedDates.find(ctx => {
                                                                            const s = DateTime.fromISO(
                                                                                ctx.startDate
                                                                            ).startOf("day");
                                                                            const e = DateTime.fromISO(
                                                                                ctx.endDate
                                                                            ).startOf("day");
                                                                            const d = day.startOf("day");
                                                                            return d >= s && d <= e;
                                                                        });
                                                                        if (!blockCtx) return null;
                                                                        const color = blockCtx.color || "#999999";
                                                                        return (
                                                                            <div
                                                                                className="bwv-block-overlay"
                                                                                style={{ "--period-color": color }}
                                                                                title={`${BLOCK_TYPE_LABELS[blockCtx.context] || blockCtx.context}: ${blockCtx.reason}`}
                                                                            >
                                                                                <Icon
                                                                                    path={mdiCalendarRemove}
                                                                                    size={0.9}
                                                                                />
                                                                                <span>
                                                                                    {BLOCK_TYPE_LABELS[
                                                                                        blockCtx.context
                                                                                    ] || blockCtx.context}
                                                                                </span>
                                                                                <span className="bwv-block-overlay-reason">
                                                                                    {blockCtx.reason}
                                                                                </span>
                                                                                <span
                                                                                    className="bwv-block-overlay-hint"
                                                                                    style={{
                                                                                        pointerEvents: "auto",
                                                                                        cursor: "pointer",
                                                                                        textDecoration: "underline",
                                                                                    }}
                                                                                    onClick={() =>
                                                                                        navigate(
                                                                                            "/instructor-profile-setup/block-dates"
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    Go to Block Dates to edit
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    })()}

                                                                {/* Sick day overlay — home mode only, lower priority than travel/block */}
                                                                {mode === "home" &&
                                                                    (() => {
                                                                        const hasTravelOverlay = travelPeriods.some(
                                                                            p => {
                                                                                const s = DateTime.fromISO(
                                                                                    p.startDate
                                                                                ).startOf("day");
                                                                                const e = DateTime.fromISO(
                                                                                    p.endDate
                                                                                ).startOf("day");
                                                                                const d = day.startOf("day");
                                                                                return d >= s && d <= e;
                                                                            }
                                                                        );
                                                                        if (hasTravelOverlay) return null;
                                                                        const hasBlockOverlay = blockedDates.some(
                                                                            ctx => {
                                                                                const s = DateTime.fromISO(
                                                                                    ctx.startDate
                                                                                ).startOf("day");
                                                                                const e = DateTime.fromISO(
                                                                                    ctx.endDate
                                                                                ).startOf("day");
                                                                                const d = day.startOf("day");
                                                                                return d >= s && d <= e;
                                                                            }
                                                                        );
                                                                        if (hasBlockOverlay) return null;
                                                                        const sickDay = sickDays.find(
                                                                            s => s.date === day.toISODate()
                                                                        );
                                                                        if (!sickDay) return null;
                                                                        return (
                                                                            <div
                                                                                className="bwv-sick-overlay"
                                                                                title={`Sick Day${sickDay.reason ? ": " + sickDay.reason : ""}`}
                                                                            >
                                                                                <Icon path={mdiMedicalBag} size={0.9} />
                                                                                <span>Sick Day</span>
                                                                                {sickDay.reason && (
                                                                                    <span className="bwv-sick-overlay-reason">
                                                                                        {sickDay.reason}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })()}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </main>
                    </div>
                </div>
            </div>

            {/* Weekly Schedule Modal */}
            {showWeeklyScheduleModal && (
                <WeeklyScheduleModal
                    isOpen={showWeeklyScheduleModal}
                    defaultLocationName={teachingLocations.find(l => l.isDefault && l.isActive)?.description || null}
                    onConfirm={handleScheduleCreated}
                    onCancel={() => setShowWeeklyScheduleModal(false)}
                />
            )}

            {/* One-Time Override Modal */}
            {showAddHomeModal && (
                <AddSlotsModal
                    isOpen={showAddHomeModal}
                    date={addHomeDate}
                    prefillStartTime={addHomePrefillTime}
                    daySlotsLookup={calendarData}
                    travelPeriod={mode === "travel" ? selectedTravelPeriod : null}
                    onConfirm={handleAddHomeSlots}
                    onNudge={handleNudgeHomeSlots}
                    onCancel={closeAddHomeModal}
                />
            )}

            {/* Phase 2.8: Travel Period Modal */}
            {showTravelModal && (
                <TravelPeriodModal
                    isOpen={showTravelModal}
                    onClose={closeTravelModal}
                    onSave={handleSaveTravelPeriod}
                    initialData={editingPeriod}
                    defaultCurrency={orgCurrency}
                    defaultRate={defaultHourlyRate}
                    teachingLocations={teachingLocations}
                    onRequestUnlockCancel={openUnlockCancel}
                    lockRefreshKey={lockRefreshKey}
                />
            )}

            {/* Currency unlock — cancel a locked travel period's bookings. Rendered as a
                SIBLING of TravelPeriodModal (lifted state), which stays open underneath. */}
            {showUnlockCancelModal && (
                <CancelTravelPeriodModal
                    isOpen={showUnlockCancelModal}
                    onClose={closeUnlockCancel}
                    onConfirm={handleConfirmUnlockCancel}
                    affectedBookings={unlockBookings}
                    isProcessing={isUnlockProcessing}
                    title="Cancel Bookings to Change Currency"
                    confirmLabel="Cancel These Bookings"
                    processingLabel="Cancelling..."
                    warningText={`To change this travel period's currency, its ${unlockBookings.length} booking${unlockBookings.length !== 1 ? "s" : ""} must be cancelled first. Students who have already paid must be refunded or given credit.`}
                />
            )}

            {/* Travel Availability Setup Modal (auto-opens after travel period creation) */}
            {showAvailabilitySetupModal && availabilitySetupPeriod && (
                <TravelAvailabilitySetupModal
                    isOpen={showAvailabilitySetupModal}
                    travelPeriod={availabilitySetupPeriod}
                    onSave={success => {
                        if (success) {
                            loadAvailabilitySlots(); // Refresh calendar
                        }
                    }}
                    onClose={() => {
                        setShowAvailabilitySetupModal(false);
                        setAvailabilitySetupPeriod(null);
                    }}
                />
            )}

            {/* Edit Slots Modal — home AND travel (mode-aware via travelPeriod).
                Travel slots are the same availability_overrides docs, so both modes
                share this one modal. */}
            {showEditHomeModal && editingDate && (
                <EditSlotsModal
                    isOpen={showEditHomeModal}
                    date={editingDate}
                    overrideId={editingOverrideId}
                    existingSlots={calendarData?.[editingDate] || []}
                    dayOfWeek={editingDayOfWeek}
                    travelPeriod={mode === "travel" ? selectedTravelPeriod : null}
                    onApply={handleApplyHomeEdits}
                    onCancel={() => {
                        setShowEditHomeModal(false);
                        setEditingDate(null);
                        setEditingOverrideId(null);
                        setEditingDayOfWeek(null);
                    }}
                />
            )}

            {/* Travel day builder — multi-slot-per-day. Opened directly by clicking the
                full-day ghost on an EMPTY travel day. Once the day has ≥1 slot the ghost
                is gone and the per-hour + cells take over for single adds. Reuses the
                setup modal in single-date mode (same day-rows editor, save built in). */}
            {builderDate && selectedTravelPeriod && (
                <TravelAvailabilitySetupModal
                    isOpen={true}
                    travelPeriod={selectedTravelPeriod}
                    singleDate={builderDate}
                    onSave={success => {
                        if (success) {
                            loadAvailabilitySlots();
                        }
                    }}
                    onClose={() => setBuilderDate(null)}
                />
            )}

            {/* Sick Day Modal */}
            {showSickDayModal && (
                <SickDayConfirmModal
                    isOpen={showSickDayModal}
                    onClose={() => setShowSickDayModal(false)}
                    onConfirm={handleConfirmSickDay}
                    onDateChange={dateIso => setTodaysBookings(getSickDayBookingsForDate(dateIso))}
                    todaysBookings={todaysBookings}
                    initialDate={DateTime.local().toISODate()}
                    isProcessing={isSickDayProcessing}
                />
            )}

            {/* Simple delete confirm (0 bookings path) */}
            {showDeleteTravelModal && (
                <ConfirmationModal
                    isOpen
                    title="Delete Travel Period"
                    message="Are you sure you want to delete this travel period? This action cannot be undone."
                    variant="danger"
                    onConfirm={confirmDeleteTravelPeriod}
                    onCancel={cancelDeleteTravelPeriod}
                />
            )}

            {/* Overlap warning — shown when new/edited travel period overlaps an existing one */}
            {showOverlapWarning && (
                <ConfirmationModal
                    isOpen
                    title="Dates Overlap an Existing Period"
                    message={(() => {
                        const fmt = iso => DateTime.fromISO(iso.slice(0, 10)).toFormat("EEE d MMM");
                        const lines = overlappingPeriods.map(p => {
                            const name = p.destination || p.reason || "Existing period";
                            const overlapStart =
                                pendingTravelPeriod?.startDate.slice(0, 10) > p.startDate.slice(0, 10)
                                    ? pendingTravelPeriod.startDate.slice(0, 10)
                                    : p.startDate.slice(0, 10);
                            const overlapEnd =
                                pendingTravelPeriod?.endDate.slice(0, 10) < p.endDate.slice(0, 10)
                                    ? pendingTravelPeriod.endDate.slice(0, 10)
                                    : p.endDate.slice(0, 10);
                            const sameDay = overlapStart === overlapEnd;
                            const overlapStr = sameDay
                                ? fmt(overlapStart)
                                : `${fmt(overlapStart)} to ${fmt(overlapEnd)}`;
                            return `"${name}": shared day${sameDay ? "" : "s"} ${overlapStr}`;
                        });
                        return `${lines.join("\n")}\n\nProceed if intentional (e.g. a changeover day with no slots set), or go back to fix the dates.`;
                    })()}
                    variant="warning"
                    confirmText="Proceed Anyway"
                    cancelText="Go Back"
                    maxWidth={480}
                    onConfirm={handleConfirmOverlap}
                    onCancel={handleCancelOverlap}
                />
            )}

            {/* Home booking conflict — pre-save gate for both create and edit paths */}
            {showHomeBookingConflict && (
                <CancelTravelPeriodModal
                    isOpen={showHomeBookingConflict}
                    onClose={handleCloseConflictModal}
                    onConfirm={handleConfirmConflictCancel}
                    affectedBookings={homeBookingConflicts}
                    isProcessing={isConflictCancelProcessing}
                    title="Bookings Clash With These Dates"
                    confirmLabel="Cancel These Bookings"
                    processingLabel="Cancelling..."
                    onKeep={handleKeepConflicts}
                    warningText={homeBookingConflicts.length === 1
                        ? "You have 1 active booking in these dates. If you keep it, that time stays booked and is left out of this trip's open availability. Or cancel it, or go back and change the dates."
                        : `You have ${homeBookingConflicts.length} active bookings in these dates. If you keep them, those times stay booked and are left out of this trip's open availability. Or cancel them, or go back and change the dates.`}
                />
            )}

            {/* Cancel-and-delete modal (N bookings path) */}
            {showCancelTravelModal && (
                <CancelTravelPeriodModal
                    isOpen={showCancelTravelModal}
                    onClose={handleCloseCancelTravelModal}
                    onConfirm={handleConfirmCancelAndDelete}
                    affectedBookings={affectedBookings}
                    isProcessing={isCancelDeleteProcessing}
                />
            )}

            {/* Help Modal */}
            <AvailabilityHelpModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} />

            {/* Edit Weekly Schedule Modal */}
            <EditWeeklyScheduleModal
                isOpen={showEditScheduleModal}
                schedule={editingScheduleGroup}
                onSaved={async () => {
                    setShowEditScheduleModal(false);
                    setEditingScheduleGroup(null);
                    await loadAvailabilitySlots();
                    try {
                        const schedules = await availabilityApi.getRecurringSchedules(user.organisationId);
                        setRecurringSchedules(schedules);
                    } catch (error) {
                        console.error("Error reloading schedules after edit:", error);
                    }
                }}
                onCancel={() => {
                    setShowEditScheduleModal(false);
                    setEditingScheduleGroup(null);
                }}
            />

            {/* Phase 5: Week Time Window Edit Modal (sidebar card editing) */}

            {confirmModal && (
                <ConfirmationModal
                    isOpen
                    title={confirmModal.title}
                    message={confirmModal.message}
                    variant={confirmModal.variant}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
        </div>
    );
};

export default ManageAvailabilityUnified;
